import { LocalVideoTrack } from 'livekit-client';
import { BackgroundProcessorWrapper, ProcessorInfo } from '../types';
import { IBackgroundProcessorConfigProvider } from '../types/backgroundProcessor';
import { checkIsSameProcessorApplied } from '../utils/backgroundProcessor';
import { BackgroundProcessorFactory } from './BackgroundProcessorFactory';

export class TrackProcessorManager {
    private processor: BackgroundProcessorWrapper | null = null;
    private currentTrack: LocalVideoTrack | null = null;
    private currentProcessorInfo: ProcessorInfo | null = null;

    public constructor(
        private configProvider: IBackgroundProcessorConfigProvider,
        private processorFactory: BackgroundProcessorFactory,
    ) {}

    /**
     * Применяет процессор к треку
     */
    public async applyToTrack(track: LocalVideoTrack | null): Promise<void> {
        if (!track) {
            this.currentTrack = null;
            return;
        }

        const processorInfo = this.configProvider.getProcessorInfo();

        // Если процессор не выбран, удаляем текущий
        if (!processorInfo) {
            await this.removeProcessor();
            this.currentTrack = track;
            return;
        }

        // Создаём или обновляем процессор
        if (!this.processor) {
            await this.createProcessor();
        } else {
            await this.updateProcessorIfNeeded(processorInfo);
        }

        // Применяем к треку
        await this.applyProcessorToTrack(track);
        this.currentTrack = track;
    }

    /**
     * Создаёт новый процессор
     */
    public async createProcessor(): Promise<void> {
        const processorInfo = this.configProvider.getProcessorInfo();
        this.processor = this.processorFactory.createProcessor();
        this.currentProcessorInfo = processorInfo;
    }

    /**
     * Обновляет процессор, если выбран другой
     */
    private async updateProcessorIfNeeded(newProcessorInfo: ProcessorInfo): Promise<void> {
        if (!this.processor) {
            return;
        }

        const isSame = checkIsSameProcessorApplied(this.currentProcessorInfo, newProcessorInfo);

        if (isSame) {
            console.warn('The same background processor is already applied');
            return;
        }

        const newOptions = this.configProvider.getProcessorOptions();

        if (newOptions) {
            await this.processorFactory.updateProcessor(this.processor, newOptions);
            this.currentProcessorInfo = newProcessorInfo;
        }
    }

    /**
     * Применяет процессор к треку
     */
    private async applyProcessorToTrack(track: LocalVideoTrack): Promise<void> {
        if (!this.processor) {
            console.error('No processor available for applyProcessor');
            return;
        }

        const hasProcessor = Boolean(track.getProcessor());
        if (hasProcessor) {
            return;
        }

        // Применяем новый процессор
        await track.setProcessor(this.processor);
    }

    /**
     * Удаляет процессор с трека
     */
    public async removeProcessor(): Promise<void> {
        if (!this.currentTrack || !this.processor) {
            return;
        }

        try {
            await this.processorFactory.switchToDisabled(this.processor);
            await this.currentTrack.stopProcessor();
        } catch (error) {
            console.warn('Error removing processor:', error);
        }
    }

    /**
     * Уничтожает менеджер и освобождает ресурсы
     */
    public destroy(): void {
        this.processor?.destroy();
        this.processor = null;
        this.currentTrack = null;
        this.currentProcessorInfo = null;
    }

    /**
     * Возвращает текущий процессор
     */
    public getProcessor(): BackgroundProcessorWrapper | null {
        return this.processor;
    }

    /**
     * Возвращает текущий трек
     */
    public getCurrentTrack(): LocalVideoTrack | null {
        return this.currentTrack;
    }
}
